import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from '../../../../localization/useTranslation';
import T from '../../../../localization/LocalizationKeys';
import _ from 'lodash';
import ModelType from '../../../Model/ModelType';
import { semanticUnits, PRIMITIVE_SCHEMAS } from '../../../../utils/dtdl';
import ContentTable from '../utils/ContentTable';
import { dtdlNameError } from '../utils/model';

const columns = [
  {
    label: T.model.name,
    path: 'name',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.model.displayName,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.model.semanticType,
    path: 'semanticType',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.model.unit,
    path: 'semanticUnit',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.model.schema,
    path: 'schema'
  },
  {
    label: T.model.description,
    path: 'description',
    searchItem: true
  },
  {
    label: T.model.comment,
    path: 'comment',
    searchItem: true
  }
];


export const TelemetryTab = ({ telemetry, onTelemetryUpdate, currentNames, modelDraft }) => {
  const T = useTranslation();
  const [_telemetry, setTelemetry] = useState(telemetry);
  const currentlyEditingItem = useRef({});
  const [content, setContent] = useState({});

  const nameHasError = useCallback((name) => {
    return dtdlNameError(name, _.reject(currentNames, n => currentlyEditingItem.current.name === n));
  }, [currentNames]);

  useEffect(() => {
    setTelemetry(telemetry);
  }, [telemetry]);

  const telemetryModel = useMemo(() => {
    const getSemanticUnits = (semanticType) => {
      if (!_.isEmpty(semanticType)) {
        const l = Object.values(semanticUnits[semanticType]);
        return _.map(l, (value) => ({ value, text: value }));
      }
    };
    const allowSemanticType = (type) => (_.includes(['double', 'float', 'integer', 'long'], type) ? true : false);
    const top = [
      {
        type: ModelType.TEXT,
        label: T.model.name,
        path: 'name',
        isRequired: true,
        isReadOnly: modelDraft === false,
        hasError: nameHasError
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.model.schema,
        path: 'schema',
        isRequired: true,
        disabled: modelDraft === false,
        hasError: _.isEmpty,
        options: _.map(PRIMITIVE_SCHEMAS, (value) => ({ value, text: value })),
        properties: {
          isTypeahead: true
        }
      },
      {
        type: ModelType.TEXT,
        label: T.model.displayName,
        path: 'displayName',
        isReadOnly: modelDraft === false,
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXT,
        label: T.model.description,
        path: 'description',
        isRequired: false,
        isReadOnly: modelDraft === false
      }
    ];
    const semantic = [
      {
        type: ModelType.OPTIONSV2,
        label: T.model.semanticType,
        path: 'semanticType',
        options: _.map(_.keys(semanticUnits), (value) => ({ value, text: value })),
        isRequired: false,
        disabled: modelDraft === false,
        properties: {
          isClearable: true,
          isTypeahead: true
        }
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.model.unit,
        path: 'semanticUnit',
        isRequired: false,
        disabled: modelDraft === false,
        options: getSemanticUnits(content?.semanticType),
        properties: {
          isClearable: true,
          isTypeahead: true
        }
      }
    ];

    return [
      ...top,
      ...(allowSemanticType(content?.schema) ? semantic : []),
      {
        type: ModelType.TEXT,
        label: T.model.comment,
        path: 'comment'
      }
    ];

  }, [T.model.comment, T.model.description, T.model.displayName, T.model.name, T.model.schema, T.model.semanticType, T.model.unit, content?.schema, content?.semanticType, modelDraft, nameHasError]);

  const onSave = useCallback((content) => {
    const cleaned = _.map(content, (t) => (_.isEmpty(t.semanticType)) ? { ...t, semanticUnit: null } : t);
    onTelemetryUpdate(cleaned);
    setTelemetry(cleaned);
  }, [onTelemetryUpdate]);

  return (
    <>
      <ContentTable
        title={T.model.tab.telemetry}
        columns={columns}
        data={_telemetry}
        dialogModel={telemetryModel}
        updatedContent={setContent}
        onSave={onSave}
        currentlyEditing={item => {
          currentlyEditingItem.current = item;
        }}
        modelDraft={modelDraft}
      />
    </>
  );
};
